import { useCallback, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { useIntl } from 'react-intl';

import { toastErrors } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { delay } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import {
  AUDIO_CONFIG,
  getTranscriptorErrorMessage,
  getTranscriptorErrorText,
  TRANSCRIPTOR_LANGUAGE,
  transcriptorModel,
} from '@/entities/transcriptor';

import {
  TranscriptorRecordingState,
  transcriptorRecordingMessages,
} from '../config';

const consultationFetchRetryDelays = [500, 1000, 2000, 4000];

type UseFileUploadParams = {
  patientId?: string;
  schemaId: string | undefined;
  selectedOnDemandSeriesIds?: string[];
  language: TRANSCRIPTOR_LANGUAGE;
  encounterId?: string;
  onClose: () => void;
};

type UseFileUploadReturn = {
  uploadedFile: File | null;
  isUploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
  isProcessing: boolean;
  handleFileUpload: (file: File | FileWithPath | null) => Promise<void>;
  resetUploadState: () => void;
};

const allowedExtensions = ['mp3', 'mp4', 'm4a', 'wav', 'ogg', 'webm'];

export const useFileUpload = ({
  patientId,
  schemaId,
  selectedOnDemandSeriesIds,
  language,
  encounterId,
  onClose,
}: UseFileUploadParams): UseFileUploadReturn => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [uploadState, setUploadState] = useState<
    Pick<
      TranscriptorRecordingState,
      'uploadedFile' | 'isUploading' | 'uploadError' | 'isProcessing'
    > & { uploadProgress: number }
  >({
    uploadedFile: null,
    isUploading: false,
    uploadProgress: 0,
    uploadError: null,
    isProcessing: false,
  });

  const validateAudioFile = useCallback(
    (file: File): string | null => {
      const fileExtension = file.name.toLowerCase().split('.').pop();

      const supportedTypes =
        AUDIO_CONFIG.SUPPORTED_MIME_TYPES as readonly string[];

      // Check if the file has a valid MIME type
      const hasValidMimeType = supportedTypes.includes(file.type);

      // Check if the file has a valid extension (fallback for when MIME type is not detected)
      const hasValidExtension = allowedExtensions.includes(fileExtension || '');

      // Some browsers might not detect MIME type correctly, so we check both
      const isValidType =
        hasValidMimeType ||
        (hasValidExtension &&
          (file.type === 'application/octet-stream' ||
            file.type === '' ||
            file.type === 'application/x-m4a' ||
            file.type === 'audio/x-m4a' ||
            file.type === 'audio/m4a')); // Some browsers report M4A files with different MIME types

      if (!isValidType) {
        return formatMessage(
          transcriptorRecordingMessages.invalidFileTypeError,
          {
            fileType: file.type || 'unknown',
            fileName: file.name,
          },
        );
      }

      return null;
    },
    [formatMessage],
  );

  const resetUploadState = useCallback(() => {
    setUploadState({
      uploadedFile: null,
      uploadError: null,
      uploadProgress: 0,
      isUploading: false,
      isProcessing: false,
    });
  }, []);

  const handleFileUpload = useCallback(
    async (file: File | FileWithPath | null) => {
      if (!file) {
        resetUploadState();
        return;
      }

      const validationError = validateAudioFile(file);
      if (validationError) {
        setUploadState((prev) => ({
          ...prev,
          uploadError: validationError,
        }));
        return;
      }

      setUploadState((prev) => ({
        ...prev,
        uploadedFile: file,
        uploadError: null,
        isUploading: true,
        uploadProgress: 0,
      }));

      const fetchConsultation = (consultationId: string) =>
        dispatch(
          transcriptorModel.thunks.fetchConsultationById({
            consultationId,
            patientId,
          }),
        ).unwrap();

      const syncUploadedConsultation = (consultationId: string) =>
        consultationFetchRetryDelays
          .reduce(
            (chain, retryDelay) =>
              chain.catch(() =>
                delay(retryDelay).then(() => fetchConsultation(consultationId)),
              ),
            fetchConsultation(consultationId),
          )
          .catch(() => {
            if (patientId) {
              dispatch(
                transcriptorModel.thunks.fetchConsultations({ patientId }),
              );
              return;
            }

            toastCaller({
              type: 'error',
              heading: formatMessage(toastErrors.errorHeading),
              message: formatMessage(toastErrors.defaultError),
            });
          });

      try {
        const result = await dispatch(
          transcriptorModel.thunks.uploadConsultation({
            schemaId,
            selectedOnDemandSeriesIds,
            language,
            file,
            encounterId,
            patientId,
            onProgress: (progress) => {
              setUploadState((prev) => ({ ...prev, uploadProgress: progress }));
            },
          }),
        ).unwrap();

        setUploadState((prev) => ({
          ...prev,
          isUploading: false,
          isProcessing: true,
        }));

        dispatch(
          transcriptorModel.actions.setConsultationData({
            consultationId: result.consultation_id,
            fragmentId: result.fragment_id,
          }),
        );

        void syncUploadedConsultation(result.consultation_id);

        onClose();
      } catch (error) {
        const refusal = getTranscriptorErrorMessage(error);
        const errorMessage = refusal
          ? formatMessage(refusal)
          : getTranscriptorErrorText(error) ??
            formatMessage(transcriptorRecordingMessages.uploadError);

        setUploadState((prev) => ({
          ...prev,
          isUploading: false,
          uploadError: errorMessage,
        }));
      }
    },
    [
      dispatch,
      validateAudioFile,
      resetUploadState,
      schemaId,
      selectedOnDemandSeriesIds,
      language,
      encounterId,
      onClose,
      patientId,
      formatMessage,
    ],
  );

  return {
    uploadedFile: uploadState.uploadedFile,
    isUploading: uploadState.isUploading,
    uploadProgress: uploadState.uploadProgress,
    uploadError: uploadState.uploadError,
    isProcessing: uploadState.isProcessing,
    handleFileUpload,
    resetUploadState,
  };
};
